import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Pencil, CalendarIcon } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { LoadingButton } from '@/components/LoadingButton'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Label } from '@/components/ui/label'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Calendar } from '@/components/ui/calendar'
import { format } from 'date-fns'
import { cn } from '@/lib/utils'
import {
  UpdateRoleRequestRole,
  type UserRoleResponse,
} from '@/api/auth/missionImpactAuthAPI.schemas'
import {
  getGetOrganizationRolesQueryKey,
  useUpdateRole,
} from '@/api/auth/roles/roles'
import { useError } from '@/contexts/ErrorContext'
import { useLanguage } from '@/contexts/LanguageContext'

interface EditMemberRoleDialogProps {
  organizationId: string
  role: UserRoleResponse
}

/**
 * Dialog that allows administrators to update an existing member's role.
 */
export function EditMemberRoleDialog({
  organizationId,
  role,
}: EditMemberRoleDialogProps) {
  const queryClient = useQueryClient()
  const { handleApiError } = useError();
  const { t } = useLanguage()
  const [isOpen, setIsOpen] = useState(false)
  const [selectedRole, setSelectedRole] = useState<UpdateRoleRequestRole>(
    role.role as UpdateRoleRequestRole,
  )
  const [membershipExpiresUtc, setMembershipExpiresUtc] = useState<Date | undefined>(
    role.membershipExpiresUtc ? new Date(role.membershipExpiresUtc) : undefined
  )

  const { mutate: updateRole, isPending } = useUpdateRole({
    mutation: {
      onSuccess: () => {
        toast.success(t.dialogs.editUserRole.roleUpdated)
        void queryClient.invalidateQueries({
          queryKey: getGetOrganizationRolesQueryKey(organizationId),
        })
        setIsOpen(false)
      },
      onError: (error) => {
        handleApiError(error, { context: 'EditMemberRoleDialog.updateRole' })
      },
    },
  })

  const handleSubmit = () => {
    updateRole({
      orgId: organizationId,
      roleId: role.id,
      data: {
        role: selectedRole,
        membershipExpiresUtc: membershipExpiresUtc?.toISOString() ?? null,
      },
    })
  }

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open)
    if (open) {
      setSelectedRole(role.role as UpdateRoleRequestRole)
      setMembershipExpiresUtc(
        role.membershipExpiresUtc ? new Date(role.membershipExpiresUtc) : undefined
      )
    }
  }

  const hasRoleChanged = selectedRole !== role.role
  const hasExpirationChanged =
    (membershipExpiresUtc?.toISOString() ?? null) !== (role.membershipExpiresUtc ?? null)
  const hasChanges = hasRoleChanged || hasExpirationChanged

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="icon" aria-label={t.dialogs.editUserRole.triggerLabel}>
          <Pencil className="h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{t.dialogs.editUserRole.title}</DialogTitle>
          <DialogDescription>
            {t.dialogs.editUserRole.description.replace('{email}', role.email ?? '')}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="edit-role-select">
              {t.dialogs.editUserRole.roleLabel}
            </Label>
            <Select
              value={selectedRole}
              onValueChange={(value) =>
                { setSelectedRole(value as UpdateRoleRequestRole); }
              }
            >
              <SelectTrigger id="edit-role-select">
                <SelectValue placeholder={t.dialogs.editUserRole.rolePlaceholder} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={UpdateRoleRequestRole.Reader}>
                  {t.dialogs.inviteUser.roleReader}
                </SelectItem>
                <SelectItem value={UpdateRoleRequestRole.Operator}>
                  {t.dialogs.inviteUser.roleOperator}
                </SelectItem>
                <SelectItem value={UpdateRoleRequestRole.Administrator}>
                  {t.dialogs.inviteUser.roleAdministrator}
                </SelectItem>
                <SelectItem value={UpdateRoleRequestRole.Billing}>
                  {t.dialogs.inviteUser.roleBillingManager}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="membership-expiration">{t.dialogs.editUserRole.membershipExpiration}</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  id="membership-expiration"
                  variant="outline"
                  className={cn(
                    'justify-start text-left font-normal',
                    !membershipExpiresUtc && 'text-muted-foreground'
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {membershipExpiresUtc ? format(membershipExpiresUtc, 'PPP') : t.dialogs.editUserRole.membershipExpirationIndefinite}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <div className="min-h-[320px]">
                  <Calendar
                    mode="single"
                    selected={membershipExpiresUtc}
                    onSelect={setMembershipExpiresUtc}
                    disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                    autoFocus
                  />
                </div>
              </PopoverContent>
            </Popover>
            {membershipExpiresUtc && (
              <div className="flex items-center justify-between">
                <p className="text-xs text-muted-foreground">
                  {t.dialogs.editUserRole.membershipExpiresOn.replace('{date}', format(membershipExpiresUtc, 'PPP'))}
                </p>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-auto p-0 text-xs"
                  onClick={() => { setMembershipExpiresUtc(undefined); }}
                >
                  {t.dialogs.editUserRole.clearExpiration}
                </Button>
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => { setIsOpen(false); }}
            disabled={isPending}
          >
            {t.dialogs.editUserRole.cancel}
          </Button>
          <LoadingButton
            type="button"
            onClick={handleSubmit}
            disabled={!hasChanges}
            isLoading={isPending}
          >
            {t.dialogs.editUserRole.update}
          </LoadingButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
